import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Joi from 'joi'
import { Grid, Stepper, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { CheckboxField, TextField } from '@e3dc-react/shell/Elements/InputFields'
import { useCountryOptions, useUstIdCountries } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useValidation } from '@e3dc-react/shell/Hooks/useValidation/useValidation'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCoordinates } from '@e3dc-react/shell/Hooks/useCoordinates'
import { useFetchUserData } from 'src/Hooks/useFetchUserData'
import { useUser } from 'src/Hooks/useUser'
import { Box } from '@mui/material'
import LinkVariant from '../Buttons/LinkVariant'
import { SHOWROOM_LINK, SHOWROOM_WORD_IN_ALL_TRANSLATIONS } from 'src/Configuration/addresses'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { useCookies } from 'react-cookie'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { CoordinatesRequestData } from '@e3dc-react/shell/Libs/Maps/types'

const useStyles = makeStyles()(theme => ({
  formItem: {
    marginBottom: theme.spacing(2),
  },
  stepsIndicator: {
    position: 'absolute',
    bottom: '55px',
    left: '50%',
  },
  showRoom: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  showRoomLink: {
    color: theme.palette.primary.main,
  },
}))

interface CompanyEditorProps {
  place: number
  onClose: () => void
  userId?: number
}

interface CompanyForm {
  Name?: string
  showroom?: boolean
  Ust_Country?: string
  Ust_ID?: string
  PLZ?: string
  Land?: string
  Address?: string
  PlaceOR?: string
  Telefon?: string
  Telefax?: string
  Email?: string
}

/**
 * A list of validation schema.
 *
 * The UST-ID is following a standard format, which can be
 * found e.g. [here](https://de.wikipedia.org/wiki/Umsatzsteuer-Identifikationsnummer)
 *
 *
 */
const validationSchemaAllSteps = Joi.object().keys({
  Name: Joi.string().required(),
  Ust_ID: Joi.string().alphanum().min(8).max(12).required(),
  Ust_Country: Joi.string().required(),
  PLZ: Joi.string().required(),
  Land: Joi.string().required(),
  Address: Joi.string().required(),
  PlaceOR: Joi.string().required(),
  Telefax: Joi.string().allow(null, ''),
  Telefon: Joi.string().required(),
  Email: Joi.string().required(),
  showroom: Joi.boolean(),
})

/**
 * The company editor allows the user to add a new company in two steps
 * Currently two requirements must be met: (1) the user is not assigned to a company already
 * (2) the user has access to a valid "umsatzsteuer-identifikationsnummer" (a unique identifier for companies)
 *
 * TODO: validate first step before allowing the user to get to the second step (BE changes required)
 */
const CompanyEditor: React.FC<CompanyEditorProps> = ({ place, onClose, userId }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const navigate = useNavigate()
  const authFetch = useAuthFetchWithResult()
  const ustCountries = useUstIdCountries()
  const [companyForm, setCompanyForm] = useState<CompanyForm>({
    Ust_Country: 'DE',
    Ust_ID: undefined,
    Name: undefined,
  })
  const [validationActive, setValidationActive] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { fetchCompany } = useFetchUserData()
  const { setUserCompany } = useUser()
  const { allCountries } = useCountryOptions()

  const validationObjectAllSteps = useValidation(companyForm, validationSchemaAllSteps)
  const validation = useMemo(() => {
    return validationActive ? validationObjectAllSteps : {}
  }, [validationActive, validationObjectAllSteps])
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const [coordinatesPayload, setCoordinatesPayload] = useState<CoordinatesRequestData>()
  const [cookies] = useCookies(['googleAgreed'])
  const googleAgreed = cookies.googleAgreed === 'true'
  const [coordinates, geocodeError] = useCoordinates(coordinatesPayload, process.env.REACT_APP_MAPS_TOKEN ?? '', googleAgreed)
  const [activeStep, setActiveStep] = useState<number>(0)

  useEffect(() => {
    if (activeStep === 1 && (validation?.Name || validation?.Ust_Country || validation?.Ust_ID)) {
      setActiveStep(0)
    }
  }, [validation, activeStep])

  useEffect(() => {
    if (companyForm?.PlaceOR && companyForm?.Land && companyForm?.Address && companyForm?.PLZ) {
      setCoordinatesPayload({
        countryCode: companyForm?.Land,
        streetName: companyForm?.PlaceOR,
        city: companyForm?.Address,
        zip: companyForm?.PLZ,
      })
    }
  }, [companyForm?.PlaceOR, companyForm?.Land, companyForm?.Address, companyForm?.PLZ])

  const onNextStep = useCallback(() => {
    setActiveStep(1)
  }, [])

  const onBackToStart = useCallback(() => {
    setActiveStep(0)
  }, [])

  const updateCompanyData = useCallback(
    (newValue?: Partial<CompanyForm>): void => {
      setCompanyForm({ ...companyForm, ...newValue })
    },
    [companyForm]
  )

  /**
   * Saving the entered company data
   *
   * If the user agreed to the Google cookies, the entered address information are checked through extracting the
   * matching coordinates by using the Google map api.
   *
   */
  const onSave = (): void => {
    setIsSaving(true)
    setValidationActive(true)

    if ((googleAgreed && !coordinates) || geocodeError) {
      setIsSaving(false)
      enqueueErrorStack(t('errors.coordinatesCouldNotResolved'))
      return
    }

    if (Object.keys(validationObjectAllSteps).length > 0) {
      setIsSaving(false)
    }

    if (Object.keys(validationObjectAllSteps).length === 0 && !geocodeError) {
      const payload = {
        ...companyForm,
        showroom: companyForm?.showroom,
        gpsLatitude: coordinates?.latitude,
        gpsLongitude: coordinates?.longitude,
        Manager: userId,
      }

      authFetch<number>({ path: 'users/companies', method: 'POST', payload: payload })
        .then(companyId => {
          enqueueSuccessStack(t('user.company.save.success'))
          if (companyId) {
            fetchCompany(companyId).then(company => {
              if (company) {
                setUserCompany(company)
              }
            })
          }
          onClose()
          navigate('/company/profile')
        })
        .catch((error: AuthFetchError) => {
          if (error.response?.data.errno === FetchErrorCodes.CompanyUstIdIsNotValid) {
            setActiveStep(0)
            enqueueErrorStack(t('user.company.save.wrongUstid'))
          } else {
            enqueueErrorStack(t('user.company.save.error'))
          }
        })
        .finally(() => setIsSaving(false))
    }
  }

  /**
   * The showroom translation should always include the word "showroom" (SHOWROOM_WORD_IN_ALL_TRANSLATIONS)
   * which should work as a link for different languages
   */
  const getShowRoomText = (): JSX.Element[] => {
    const textArray = t('companyEditor.showRoomLink').split(' ')

    return textArray.map((word, index) => {
      if (SHOWROOM_WORD_IN_ALL_TRANSLATIONS.toLowerCase() === word.toLowerCase()) {
        return <LinkVariant key={index} linkClassName={classes.showRoomLink} label={word} type="newTab" link={SHOWROOM_LINK} hasUnderline={false} />
      }
      return <Typography key={index}>{word}</Typography>
    })
  }

  const steps = [
    {
      stepId: 0,
      content: (
        <Grid container>
          <Grid item xs={12}>
            <TextField
              label={`${t('companyEditor.companyName')}`}
              required
              value={companyForm?.Name}
              onChange={(value: string) => updateCompanyData({ Name: value })}
              className={classes.formItem}
              helperText={validation['Name'] ? validation['Name'].message : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Select
              label={t('companyEditor.ustCountry')}
              value={companyForm?.Ust_Country}
              options={ustCountries}
              onChange={(value: string) => updateCompanyData({ Ust_Country: value })}
              className={classes.formItem}
              helperText={validation['Ust_Country'] ? validation['Ust_Country'].message : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={10}>
            <TextField
              label={`${t('companyEditor.ustId')}`}
              required
              value={companyForm?.Ust_ID}
              onChange={(value: string) => updateCompanyData({ Ust_ID: value })}
              className={classes.formItem}
              helperText={validation['Ust_ID'] ? validation['Ust_ID'].message : undefined}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      stepId: 1,
      content: (
        <Grid container justifyContent="space-between">
          <Grid item xs={12}>
            <TextField
              label={`${t('common.address')}`}
              required
              value={companyForm?.PlaceOR}
              onChange={(value: string) => updateCompanyData({ PlaceOR: value })}
              className={classes.formItem}
              helperText={validation['PlaceOR'] ? validation['PlaceOR'].message : undefined}
            />
          </Grid>
          <Grid item xs={5} sm={6}>
            <TextField
              label={`${t('common.zipCode')}`}
              required
              value={companyForm?.PLZ}
              onChange={(value: string) => updateCompanyData({ PLZ: value })}
              fullWidth
              className={classes.formItem}
              helperText={validation['PLZ'] ? validation['PLZ'].message : undefined}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={`${t('common.city')}`}
              required
              value={companyForm?.Address}
              onChange={(value: string) => updateCompanyData({ Address: value })}
              fullWidth
              className={classes.formItem}
              helperText={validation['Address'] ? validation['Address'].message : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <Select
              label={`${t('common.country')}`}
              required
              value={companyForm?.Land}
              onChange={(value: string) => updateCompanyData({ Land: value })}
              options={allCountries}
              className={classes.formItem}
              helperText={validation['Land'] ? validation['Land'].message : undefined}
              isSearchable
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={`${t('common.phone')}`}
              required
              value={companyForm?.Telefon}
              onChange={(value: string) => updateCompanyData({ Telefon: value })}
              className={classes.formItem}
              helperText={validation['Telefon'] ? validation['Telefon'].message : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={`${t('common.fax')} (${t('common.optional')})`}
              value={companyForm?.Telefax}
              onChange={(value: string) => updateCompanyData({ Telefax: value })}
              className={classes.formItem}
              helperText={validation['Telefax'] ? validation['Telefax'].message : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={`${t('common.email')}`}
              required
              value={companyForm?.Email}
              onChange={(value: string) => updateCompanyData({ Email: value })}
              className={classes.formItem}
              helperText={validation['Email'] ? validation['Email'].message : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <div className={classes.showRoom}>
              <CheckboxField value={companyForm?.showroom} onChange={(value: boolean) => updateCompanyData({ showroom: value })} />
              {getShowRoomText().map((element, index) => {
                return (
                  <Box key={index} paddingRight={1}>
                    {element}
                  </Box>
                )
              })}
            </div>
          </Grid>
        </Grid>
      ),
    },
  ]

  const getDialogActions = (): DialogActions => {
    if (activeStep === 0) {
      return {
        primary: {
          name: t('common.next'),
          onClick: onNextStep,
          isLoading: isSaving,
        },
        secondary: [
          {
            name: t('common.cancel'),
          },
        ],
      }
    }

    return {
      primary: {
        name: t('common.save'),
        onClick: onSave,
        isLoading: isSaving,
      },
      secondary: [
        {
          name: t('common.back'),
          onClick: onBackToStart,
        },
        {
          name: t('common.cancel'),
        },
      ],
    }
  }

  return (
    <Dialog title={t('user.userData.addCompany')} onClose={() => onClose()} place={place} actions={getDialogActions()}>
      <Box paddingTop={2} paddingBottom={2}>
        {steps[activeStep].content}
      </Box>

      <Typography className={classes.stepsIndicator} variant="caption">{`${activeStep + 1}/${steps.length}`}</Typography>

      <Stepper activeStep={activeStep} />
    </Dialog>
  )
}

export default CompanyEditor
